import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { adminTotpSetup, adminTotpEnable } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Copy, ArrowRight } from "lucide-react";

export default function AdminSetup() {
  const navigate = useNavigate();
  const [setup, setSetup] = useState(null); // {qr_data_url, secret, otpauth_url, account, issuer}
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Guard against React 18 StrictMode double-invocation of the setup effect.
  const setupStartedRef = useRef(false);

  useEffect(() => {
    if (setupStartedRef.current) return;
    setupStartedRef.current = true;
    (async () => {
      try {
        const s = await adminTotpSetup();
        setSetup(s);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (typeof detail === "string" && detail.includes("ya está activado")) {
          toast.info("TOTP ya activado. Redirigiendo a verificación.");
          navigate("/admin/verify", { replace: true });
        } else {
          toast.error(typeof detail === "string" ? detail : "No se pudo iniciar el setup");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const onEnable = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      await adminTotpEnable(code.trim());
      toast.success("2FA activado correctamente");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Código incorrecto");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-foreground/60" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12" data-testid="admin-setup-page">
      <div className="w-full max-w-2xl">
        <Link to="/dashboard" className="text-[12px] text-foreground/55 hover:text-foreground transition-colors mb-6 inline-block">
          ← Volver al dashboard
        </Link>
        <div className="app-card p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-foreground text-white grid place-items-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-black leading-tight">Activa 2FA para admin</h1>
              <p className="text-xs text-foreground/55">Único paso. Cinco minutos.</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="text-[13px] font-semibold mb-2">1 · Abre tu app de autenticación</div>
              <p className="text-[13px] text-foreground/70 leading-relaxed">
                Usa <b>Google Authenticator</b>, <b>Authy</b>, <b>1Password</b> o cualquier app que genere códigos TOTP.
                Escanea este QR o pega el código secreto manualmente.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div className="rounded-2xl bg-white border border-black/[0.06] p-6 grid place-items-center">
                {setup?.qr_data_url ? (
                  <img
                    src={setup.qr_data_url}
                    alt="Código QR para configurar 2FA"
                    className="w-56 h-56"
                    data-testid="admin-setup-qr"
                  />
                ) : null}
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-1">Cuenta</div>
                  <div className="text-[13px] font-medium truncate">{setup?.account}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-1">Emisor</div>
                  <div className="text-[13px] font-medium">{setup?.issuer}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-1">Clave manual</div>
                  <button
                    type="button"
                    onClick={() => copy(setup?.secret)}
                    className="flex items-center gap-2 text-[12px] font-mono bg-foreground/[0.04] hover:bg-foreground/[0.08] transition-colors px-2.5 py-1.5 rounded-lg"
                    data-testid="admin-setup-copy-secret"
                  >
                    <span className="truncate max-w-[180px]">{setup?.secret}</span>
                    <Copy className="w-3 h-3 shrink-0" />
                  </button>
                </div>
              </div>
            </div>

            <form onSubmit={onEnable} className="pt-2 border-t border-black/[0.06] space-y-3">
              <label className="block text-[13px] font-semibold">2 · Introduce el código de 6 dígitos</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={8}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="flex-1 px-4 py-3 rounded-xl border border-black/[0.08] bg-white text-[15px] font-mono tracking-widest focus:outline-none focus:border-foreground/40"
                  data-testid="admin-setup-code-input"
                  required
                />
                <button
                  type="submit"
                  disabled={submitting || code.length < 6}
                  className="px-5 rounded-xl bg-foreground text-white text-[13px] font-medium disabled:opacity-50 flex items-center gap-2"
                  data-testid="admin-setup-enable-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Activar <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
              <p className="text-[11px] text-foreground/55">
                El código cambia cada 30 segundos. Si ves un error, prueba con el siguiente.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
